/*
 * ACR TI-RADS (2017) for the THYROID ULTRASOUND worksheet.
 *
 * Why: the AI interpretation drafts could not assign a TI-RADS level because the
 * worksheet only had free-text nodule notes (14 of 16 sampled nodules had no
 * features). The form now captures the five ACR feature categories per nodule and
 * stores both the inputs and the values computed here.
 *
 * Reference: Tessler FN et al., ACR Thyroid Imaging, Reporting and Data System
 * (TI-RADS): White Paper of the ACR TI-RADS Committee. J Am Coll Radiol 2017;14:587-595.
 *   Composition     cystic/almost completely cystic 0, spongiform 0, mixed 1, solid 2
 *   Echogenicity    anechoic 0, hyper/isoechoic 1, hypoechoic 2, very hypoechoic 3
 *   Shape           wider-than-tall 0, taller-than-wide 3
 *   Margin          smooth 0, ill-defined 0, lobulated/irregular 2, extra-thyroidal extension 3
 *   Echogenic foci  (add every one present) none/large comet-tail 0, macrocalcifications 1,
 *                   peripheral (rim) 2, punctate 3
 *   Spongiform: "do not add further points for other categories" -> total 0.
 *   Footnotes ("cannot be determined"): composition obscured by calcification 2 (scored as
 *   solid), echogenicity indeterminate 1, margin not determinable 0. Reports print these
 *   as "indeterminate", never as a descriptor that was not seen.
 *   Levels: 0 TR1, 2 TR2, 3 TR3, 4-6 TR4, >=7 TR5. The chart has no row for exactly
 *   1 point; it is placed in TR2 (not suspicious), as the ACR calculator does.
 *   Size (maximum dimension) -> FNA / follow-up:
 *     TR1, TR2  no FNA
 *     TR3       FNA >= 2.5 cm; follow-up >= 1.5 cm (1, 3 and 5 years)
 *     TR4       FNA >= 1.5 cm; follow-up >= 1.0 cm (1, 2, 3 and 5 years)
 *     TR5       FNA >= 1.0 cm; follow-up >= 0.5 cm (every year for up to 5 years)
 *
 * Stored JSON (additive keys on the imagings row; old rows simply lack them):
 *   tiradsVersion: "ACR-2017"
 *   nodulesStatus: "none" | "present"
 *   nodules: [{ lobe, pole, L, W, H, composition, echogenicity, shape, margin,
 *               echogenicFoci: [..], vascularity, notes,
 *               tirads: { points: {composition, echogenicity, shape, margin, echogenicFoci},
 *                         total, level, label, complete, maxDimensionCm,
 *                         recommendation: { action, text } } }]
 *   lymphNodes: { status: "none" | "abnormal",
 *                 nodes: [{ side, level, long, short, features: [..], notes }] }
 *
 * Pure functions only (no React), so the arithmetic is unit-tested on its own.
 */

export const TIRADS_VERSION = "ACR-2017";

export const LOBES = [
  { value: "right", label: "Right lobe" },
  { value: "left", label: "Left lobe" },
  { value: "isthmus", label: "Isthmus" },
];
export const POLES = [
  { value: "upper", label: "Upper" },
  { value: "mid", label: "Mid" },
  { value: "lower", label: "Lower" },
];

export const COMPOSITION = [
  { value: "cystic", label: "Cystic or almost completely cystic", points: 0 },
  { value: "spongiform", label: "Spongiform", points: 0 },
  { value: "mixed", label: "Mixed cystic and solid", points: 1 },
  { value: "solid", label: "Solid or almost completely solid", points: 2 },
  { value: "indeterminate", label: "Cannot be determined (calcification)", points: 2, report: "indeterminate, obscured by calcification" },
];
export const ECHOGENICITY = [
  { value: "anechoic", label: "Anechoic", points: 0 },
  { value: "hyper_iso", label: "Hyperechoic or isoechoic", points: 1 },
  { value: "hypo", label: "Hypoechoic", points: 2 },
  { value: "very_hypo", label: "Very hypoechoic", points: 3 },
  { value: "indeterminate", label: "Cannot be determined", points: 1, report: "indeterminate" },
];
export const SHAPE = [
  { value: "wider", label: "Wider-than-tall", points: 0 },
  { value: "taller", label: "Taller-than-wide", points: 3 },
];
export const MARGIN = [
  { value: "smooth", label: "Smooth", points: 0 },
  { value: "ill_defined", label: "Ill-defined", points: 0 },
  { value: "lobulated", label: "Lobulated or irregular", points: 2 },
  { value: "ete", label: "Extra-thyroidal extension", points: 3 },
  { value: "indeterminate", label: "Cannot be determined", points: 0, report: "indeterminate" },
];
export const FOCI = [
  { value: "none", label: "None or large comet-tail artifacts", points: 0 },
  { value: "macro", label: "Macrocalcifications", points: 1 },
  { value: "rim", label: "Peripheral (rim) calcifications", points: 2 },
  { value: "punctate", label: "Punctate echogenic foci", points: 3 },
];
export const VASCULARITY = [
  { value: "absent", label: "Absent" },
  { value: "peripheral", label: "Peripheral" },
  { value: "internal", label: "Internal" },
  { value: "mixed", label: "Peripheral and internal" },
];

export const LEVELS = {
  TR1: "Benign",
  TR2: "Not suspicious",
  TR3: "Mildly suspicious",
  TR4: "Moderately suspicious",
  TR5: "Highly suspicious",
};

// Size thresholds (cm, maximum dimension): [FNA if >=, follow-up if >=, follow-up schedule]
const SIZE_RULES = {
  TR3: { fna: 2.5, follow: 1.5, schedule: "ultrasound at 1, 3 and 5 years" },
  TR4: { fna: 1.5, follow: 1.0, schedule: "ultrasound at 1, 2, 3 and 5 years" },
  TR5: { fna: 1.0, follow: 0.5, schedule: "ultrasound every year for up to 5 years" },
};

export const LN_SIDES = [
  { value: "right", label: "Right" },
  { value: "left", label: "Left" },
  { value: "midline", label: "Midline" },
];
export const LN_LEVELS = ["I", "II", "III", "IV", "V", "VI", "VII"].map((v) => ({ value: v, label: `Level ${v}` }));
export const LN_STATUSES = [
  { value: "none", label: "None seen" },
  { value: "abnormal", label: "Abnormal" },
  { value: "not_assessed", label: "Not assessed" },
];
export const LN_FEATURES = [
  { value: "loss_fatty_hilum", label: "Loss of fatty hilum" },
  { value: "round", label: "Round" },
  { value: "calcification", label: "Calcification" },
  { value: "cystic", label: "Cystic change" },
  { value: "peripheral_vascularity", label: "Peripheral vascularity" },
];

const pointsOf = (list, value) => {
  const hit = list.find((o) => o.value === value);
  return hit ? hit.points : null;
};

export const labelOf = (list, value) => {
  const hit = list.find((o) => o.value === value);
  return hit ? hit.label : "";
};

// Wording for the report: "indeterminate" options never print as a descriptor.
export const reportLabelOf = (list, value) => {
  const hit = list.find((o) => o.value === value);
  return hit ? hit.report || hit.label : "";
};

// Spongiform with a suspicious feature is usually a mis-called solid nodule: ask the
// sonographer to confirm the composition (does not block Save).
export function spongiformConflicts(nodule) {
  const n = nodule || {};
  if (n.composition !== "spongiform") return [];
  const out = [];
  if (Array.isArray(n.echogenicFoci) && n.echogenicFoci.includes("punctate")) out.push("punctate echogenic foci");
  if (n.margin === "ete") out.push("extra-thyroidal extension");
  if (n.shape === "taller") out.push("taller-than-wide shape");
  return out;
}

export function levelForPoints(total) {
  if (total === null || total === undefined || Number.isNaN(total)) return null;
  if (total <= 0) return "TR1";
  if (total <= 2) return "TR2";
  if (total === 3) return "TR3";
  if (total <= 6) return "TR4";
  return "TR5";
}

// Largest of the three dimensions in cm, or null when none is a positive number.
export function maxDimensionCm(nodule) {
  const dims = ["L", "W", "H"]
    .map((k) => parseFloat(nodule && nodule[k]))
    .filter((n) => Number.isFinite(n) && n > 0);
  return dims.length ? Math.max(...dims) : null;
}

export function recommendationFor(level, maxCm) {
  if (!level) return { action: "", text: "" };
  if (level === "TR1" || level === "TR2") return { action: "NONE", text: "No FNA" };
  const rule = SIZE_RULES[level];
  if (maxCm === null || maxCm === undefined) return { action: "", text: "Enter size" };
  if (maxCm >= rule.fna) return { action: "FNA", text: `FNA recommended (≥ ${rule.fna} cm)` };
  if (maxCm >= rule.follow) return { action: "FOLLOW_UP", text: `Follow-up (≥ ${rule.follow} cm): ${rule.schedule}` };
  return { action: "NONE", text: `No FNA or follow-up (< ${rule.follow} cm)` };
}

export function computeTirads(nodule) {
  const n = nodule || {};
  const foci = Array.isArray(n.echogenicFoci) ? n.echogenicFoci : [];
  const points = {
    composition: pointsOf(COMPOSITION, n.composition),
    echogenicity: pointsOf(ECHOGENICITY, n.echogenicity),
    shape: pointsOf(SHAPE, n.shape),
    margin: pointsOf(MARGIN, n.margin),
    echogenicFoci: foci.length ? foci.reduce((s, f) => s + (pointsOf(FOCI, f) || 0), 0) : null,
  };
  const maxCm = maxDimensionCm(n);
  let total = null;
  let complete;
  if (n.composition === "spongiform") {
    // ACR: spongiform scores 0 and no points are added for the other categories
    complete = true;
    total = 0;
    points.echogenicity = 0;
    points.shape = 0;
    points.margin = 0;
    points.echogenicFoci = 0;
  } else {
    complete = Object.values(points).every((p) => p !== null);
    if (complete) total = Object.values(points).reduce((s, p) => s + p, 0);
  }
  const level = complete ? levelForPoints(total) : null;
  return {
    points,
    total,
    level,
    label: level ? LEVELS[level] : "",
    complete,
    maxDimensionCm: maxCm,
    recommendation: recommendationFor(level, maxCm),
  };
}

export const emptyNodule = () => ({
  lobe: "",
  pole: "",
  L: "",
  W: "",
  H: "",
  composition: "",
  echogenicity: "",
  shape: "",
  margin: "",
  echogenicFoci: [],
  vascularity: "",
  notes: "",
  spongiformConfirmed: false,
});

export const emptyLymphNode = () => ({ side: "", level: "", long: "", short: "", features: [], notes: "" });

// Echogenic foci are a multi-select, but "none / comet-tail" excludes the others.
export function toggleFocus(selected, value) {
  const cur = Array.isArray(selected) ? selected : [];
  if (cur.includes(value)) return cur.filter((v) => v !== value);
  if (value === "none") return ["none"];
  return [...cur.filter((v) => v !== "none"), value];
}

export function toggleValue(selected, value) {
  const cur = Array.isArray(selected) ? selected : [];
  return cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
}

// Problems that block Save, in form order (the form shows the first, like the others).
export function validateTirads(nodulesStatus, nodules, lymphNodes) {
  const errors = [];
  if (!nodulesStatus) errors.push("Nodules: choose None seen or add a nodule.");
  if (nodulesStatus === "present") {
    if (!nodules || nodules.length === 0) errors.push("Nodules: add at least one nodule or choose None seen.");
    (nodules || []).forEach((n, i) => {
      const k = `Nodule ${i + 1}`;
      if (!n.lobe) errors.push(`${k}: location is required.`);
      ["L", "W", "H"].forEach((d) => {
        const v = parseFloat(n[d]);
        if (!(Number.isFinite(v) && v > 0)) errors.push(`${k}: ${d} (cm) is required.`);
      });
      if (!n.composition) errors.push(`${k}: composition is required.`);
      if (n.composition !== "spongiform") {
        if (!n.echogenicity) errors.push(`${k}: echogenicity is required.`);
        if (!n.shape) errors.push(`${k}: shape is required.`);
        if (!n.margin) errors.push(`${k}: margin is required.`);
        if (!n.echogenicFoci || n.echogenicFoci.length === 0) errors.push(`${k}: echogenic foci is required.`);
      }
    });
  }
  const ln = lymphNodes || {};
  if (!ln.status) errors.push("Lymph nodes: choose None seen, Abnormal or Not assessed.");
  if (ln.status === "abnormal") {
    if (!ln.nodes || ln.nodes.length === 0) errors.push("Lymph nodes: add the abnormal node.");
    (ln.nodes || []).forEach((n, i) => {
      if (!n.level) errors.push(`Lymph node ${i + 1}: level is required.`);
    });
  }
  return errors;
}

// The additive keys written into the worksheet JSON on Save.
export function buildTiradsPayload(nodulesStatus, nodules, lymphNodes) {
  const ln = lymphNodes || {};
  return {
    tiradsVersion: TIRADS_VERSION,
    nodulesStatus,
    nodules: nodulesStatus === "present" ? (nodules || []).map((n) => ({ ...n, tirads: computeTirads(n) })) : [],
    lymphNodes: { status: ln.status || "", nodes: ln.status === "abnormal" ? ln.nodes || [] : [] },
  };
}

// Has the sonographer typed anything into this card (so discarding it needs a confirm)?
export function hasEntries(obj) {
  if (!obj || typeof obj !== "object") return false;
  return Object.entries(obj).some(([k, v]) => k !== "spongiformConfirmed" && (Array.isArray(v) ? v.length > 0 : v !== "" && v !== null && v !== undefined && v !== false));
}

export const noduleLocation = (n) =>
  [labelOf(LOBES, n && n.lobe), n && n.lobe !== "isthmus" ? labelOf(POLES, n && n.pole).toLowerCase() : ""]
    .filter(Boolean)
    .join(", ");
